import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../api';
import Navbar from '../components/Navbar';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const S = {
  page:    { minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
  body:    { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  filters: { background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '12px 24px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 },
  input:   { padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", background: 'var(--bg)', color: 'var(--text)', outline: 'none' },
  select:  { padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", background: 'var(--bg)', color: 'var(--text)', outline: 'none' },
  tableWrap: { flex: 1, overflowY: 'auto', overflowX: 'auto' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th:      (sort, col) => ({
    textAlign: 'left', padding: '9px 12px', background: 'var(--surface)',
    borderBottom: '2px solid var(--border)', position: 'sticky', top: 0, zIndex: 2,
    color: sort.col === col ? 'var(--accent)' : 'var(--text-muted)',
    fontWeight: 600, fontFamily: "'DM Mono', monospace", fontSize: '10px',
    letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap',
    cursor: 'pointer', userSelect: 'none',
  }),
  td:      { padding: '9px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle' },
  // Holdings nesting
  holdingsWrap: (depth) => ({
    background: depth === 0 ? 'var(--bg)' : `rgba(0,0,0,${depth * 0.025})`,
    borderTop: '1px solid var(--border)',
  }),
  holdingsTable: { width: '100%', borderCollapse: 'collapse', fontSize: '11px' },
  hth:     { padding: '6px 12px', color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.05em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  htd:     (depth) => ({ padding: '6px 12px', paddingLeft: `${12 + depth * 20}px`, borderBottom: '1px solid var(--border)', verticalAlign: 'middle' }),
  weightBar: (pct, type) => {
    const colors = { equity: '#3B82F6', etf: 'var(--accent)', cash: '#10B981', bond: '#F59E0B', commodity: '#EF4444', other: '#8B5CF6' };
    const w = Math.min(Math.abs(pct), 100);
    return {
      display: 'inline-block', width: `${w}px`, maxWidth: '100px',
      height: '6px', borderRadius: '3px',
      background: pct < 0 ? '#EF4444' : (colors[type] || '#6B7280'),
      verticalAlign: 'middle', marginRight: '6px',
    };
  },
  badge:   (type) => {
    const map = { equity: ['#DBEAFE','#1D4ED8'], etf: ['var(--accent-dim)','var(--accent)'], cash: ['#D1FAE5','#065F46'], bond: ['#FEF3C7','#92400E'], commodity: ['#FEE2E2','#991B1B'], other: ['var(--border)','var(--text-muted)'] };
    const [bg, color] = map[type] || map.other;
    return { display: 'inline-block', padding: '1px 6px', borderRadius: '3px', fontSize: '9px', fontWeight: 600, fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', background: bg, color };
  },
  statusDot: (status) => {
    const map = { ok: '#10B981', failed: '#EF4444', pending: '#F59E0B', no_url: '#6B7280', manual: 'var(--accent)' };
    return { display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%', background: map[status] || '#6B7280', marginRight: '6px' };
  },
  chip:    (active) => ({
    padding: '4px 10px', borderRadius: '20px', border: '1px solid var(--border)',
    fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
    background: active ? 'var(--accent)' : 'var(--bg)',
    color: active ? '#fff' : 'var(--text-muted)',
    transition: 'all 0.12s',
  }),
  rangeWrap: { display: 'flex', alignItems: 'center', gap: '4px' },
  rangeInput: { width: '70px', padding: '6px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: '12px', fontFamily: "'DM Sans', sans-serif", background: 'var(--bg)', color: 'var(--text)', outline: 'none' },
  count:   { fontSize: '12px', color: 'var(--text-muted)', marginLeft: 'auto', whiteSpace: 'nowrap' },
  divider: { width: '1px', height: '20px', background: 'var(--border)', flexShrink: 0 },
  expandBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: '12px', fontFamily: "'DM Mono', monospace", padding: '0 4px' },
  noData:  { color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '11px' },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmtNum(n, dec = 2) {
  if (n == null || n === '') return '—';
  return Number(n).toFixed(dec);
}
function fmtAum(n) {
  if (n == null) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  return `$${Number(n).toLocaleString()}`;
}
function sortRows(rows, col, dir) {
  return [...rows].sort((a, b) => {
    let av = a[col], bv = b[col];
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string') return dir === 'ASC' ? av.localeCompare(bv) : bv.localeCompare(av);
    return dir === 'ASC' ? av - bv : bv - av;
  });
}

// ---------------------------------------------------------------------------
// Holdings — recursive nested table
// ---------------------------------------------------------------------------
function HoldingsTable({ holdings, depth = 0, gearingRatio = null, parentOverall = 100 }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  if (!holdings || holdings.length === 0) {
    return <div style={{ padding: '12px 24px', ...S.noData }}>No holdings data available.</div>;
  }

  const totalWeight = holdings.reduce((s, h) => s + (parseFloat(h.weight) || 0), 0);

  return (
    <div style={S.holdingsWrap(depth)}>
      <table style={S.holdingsTable}>
        <thead>
          <tr>
            <th style={{ ...S.hth, paddingLeft: `${12 + depth * 20}px`, width: '28px' }}></th>
            <th style={{ ...S.hth, paddingLeft: `${12 + depth * 20}px` }}>Holding</th>
            <th style={S.hth}>Symbol</th>
            <th style={S.hth}>Type</th>
            <th style={S.hth}>Weight %</th>
            <th style={S.hth}>Overall %</th>
            <th style={S.hth}>Bar</th>
            {gearingRatio && <th style={S.hth}>Geared %</th>}
          </tr>
        </thead>
        <tbody>
          {holdings.map(h => {
            const hasNested = h.holdings && h.holdings.length > 0;
            const isOpen = expanded[h.holding_id];
            const weight = h.weight == null || h.weight === '' || !Number.isFinite(Number(h.weight)) ? null : Number(h.weight);
            const overallWeight = weight == null || parentOverall == null ? null : parentOverall * weight / 100;
            const gearedWeight = gearingRatio && weight != null ? weight * gearingRatio : null;
            const matchedSymbol = h.matched_symbol || h.holding_symbol;
            const matchedTicker = h.matched_instrument_id && matchedSymbol && h.matched_exchange
              ? `${matchedSymbol}:${h.matched_exchange}`
              : null;

            return (
              <React.Fragment key={h.holding_id}>
                <tr style={{ background: isOpen ? 'var(--accent-dim)' : 'transparent', color: matchedTicker ? 'inherit' : 'var(--text-muted)' }}>
                  <td style={{ ...S.htd(depth), width: '28px', paddingRight: 0 }}>
                    {hasNested && (
                      <button style={S.expandBtn} onClick={() => toggle(h.holding_id)}>
                        {isOpen ? '▾' : '▸'}
                      </button>
                    )}
                  </td>
                  <td style={S.htd(depth)}>
                    {matchedTicker ? (
                      <a
                        href={`/company?ticker=${encodeURIComponent(matchedTicker)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: hasNested ? 600 : 400, color: 'var(--accent)', textDecoration: 'none' }}
                      >
                        {h.holding_name}
                      </a>
                    ) : (
                      <span style={{ fontWeight: hasNested ? 600 : 400 }}>{h.holding_name}</span>
                    )}
                    {h.is_geared && (
                      <span style={{ marginLeft: '6px', fontSize: '9px', color: 'var(--error)', fontFamily: "'DM Mono', monospace", fontWeight: 700 }}>
                        {h.gearing_ratio ? `${h.gearing_ratio}x` : 'GEARED'}
                      </span>
                    )}
                  </td>
                  <td style={{ ...S.htd(depth), fontFamily: "'DM Mono', monospace", color: matchedTicker ? 'var(--accent)' : 'var(--text-muted)' }}>
                    {matchedTicker ? (
                      <a
                        href={`/chart?t=${encodeURIComponent(matchedTicker)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}
                      >
                        {h.holding_symbol || h.matched_symbol}
                      </a>
                    ) : (
                      h.holding_symbol || '—'
                    )}
                  </td>
                  <td style={S.htd(depth)}>
                    <span style={S.badge(h.holding_type)}>{h.holding_type}</span>
                  </td>
                  <td style={{ ...S.htd(depth), fontFamily: "'DM Mono', monospace", textAlign: 'right', paddingRight: '8px' }}>
                    <span style={{ color: weight < 0 ? 'var(--error)' : 'inherit' }}>
                      {weight == null ? '—' : fmtNum(weight, 4) + '%'}
                    </span>
                  </td>
                  <td style={{ ...S.htd(depth), fontFamily: "'DM Mono', monospace", textAlign: 'right' }}>
                    {fmtNum(overallWeight, 4)}{overallWeight == null ? '' : '%'}
                  </td>
                  <td style={S.htd(depth)}>
                    <span style={S.weightBar(weight || 0, h.holding_type)} />
                    <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                      {totalWeight > 0 && weight != null ? fmtNum((weight / totalWeight) * 100, 1) + '%' : '—'}
                    </span>
                  </td>
                  {gearingRatio && (
                    <td style={{ ...S.htd(depth), fontFamily: "'DM Mono', monospace", color: 'var(--error)' }}>
                      {fmtNum(gearedWeight, 4)}%
                    </td>
                  )}
                </tr>
                {isOpen && hasNested && (
                  <tr>
                    <td colSpan={gearingRatio ? 8 : 7} style={{ padding: 0 }}>
                      <HoldingsTable
                        holdings={h.holdings}
                        depth={depth + 1}
                        gearingRatio={h.is_geared && h.gearing_ratio ? h.gearing_ratio : null}
                        parentOverall={overallWeight}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ETF detail row (expanded state under main table row)
// ---------------------------------------------------------------------------
function EtfDetailRow({ etf, colSpan }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/etf/${etf.symbol}?exchange=${etf.exchange}`);
        setDetail(r.data.etf);
      } catch {
        setErr('Failed to load holdings.');
      } finally { setLoading(false); }
    })();
  }, [etf.symbol, etf.exchange]);

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0 }}>
        <div style={{ borderTop: '2px solid var(--accent)', background: 'var(--bg)' }}>
          {/* Fund summary strip */}
          <div style={{ display: 'flex', gap: '28px', padding: '12px 24px', flexWrap: 'wrap', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
            {[
              ['Symbol',   etf.symbol],
              ['Name',     etf.name],
              ['Provider', etf.provider_name || etf.fund_provider || '—'],
              ['Exchange', etf.exchange],
              ['MER',      etf.expense_ratio != null ? `${fmtNum(etf.expense_ratio, 2)}%` : '—'],
              ['AUM',      fmtAum(etf.aum_usd)],
              ['TTM Yield', etf.ttm_yield != null ? `${fmtNum(etf.ttm_yield, 2)}%` : '—'],
              ['Index',    detail?.index_tracked || etf.index_tracked || '—'],
              ['Geared',   etf.is_geared ? `Yes (${etf.gearing_ratio}x)` : 'No'],
              ['Holdings', etf.holdings_count || '—'],
            ].map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: '9px', color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace", textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>{label}</div>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>{val}</div>
              </div>
            ))}
          </div>

          {/* Description */}
          {detail?.description && (
            <div style={{ padding: '10px 24px', fontSize: '12px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', lineHeight: '1.6', maxWidth: '900px' }}>
              {detail.description}
            </div>
          )}

          {/* Holdings workspace */}
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)' }}>
            <a
              href={'/etf/workspace?symbol=' + encodeURIComponent(etf.symbol) + '&exchange=' + encodeURIComponent(etf.exchange)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--accent)', textDecoration: 'none', fontSize: '12px', fontWeight: 700 }}
            >
              Open holdings workspace ↗
            </a>
          </div>

          {/* Holdings */}
          {loading && <div style={{ padding: '16px 24px', color: 'var(--text-muted)', fontSize: '12px' }}>Loading holdings…</div>}
          {err     && <div style={{ padding: '16px 24px', color: 'var(--error)', fontSize: '12px' }}>{err}</div>}
          {detail  && <HoldingsTable holdings={detail.holdings} depth={0} gearingRatio={detail.is_geared && detail.gearing_ratio ? detail.gearing_ratio : null} />}
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Main screener
// ---------------------------------------------------------------------------
export default function EtfScreener() {
  const [etfs, setEtfs]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [sort, setSort]  = useState({ col: 'symbol', dir: 'ASC' });

  // Filters
  const [search, setSearch]         = useState('');
  const [exchFilter, setExchFilter] = useState('');
  const [provFilter, setProvFilter] = useState('');
  const [gearedOnly, setGearedOnly] = useState(false);
  const [merMin, setMerMin]         = useState('');
  const [merMax, setMerMax]         = useState('');
  const [aumMin, setAumMin]         = useState('');
  const [holdingsMin, setHoldingsMin] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/etf/list');
        setEtfs(r.data.etfs);
      } finally { setLoading(false); }
    })();
  }, []);

  const toggleExpand = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const handleSort = (col) => {
    setSort(s => s.col === col ? { col, dir: s.dir === 'ASC' ? 'DESC' : 'ASC' } : { col, dir: 'ASC' });
  };

  const sortArrow = (col) => sort.col === col ? (sort.dir === 'ASC' ? ' ▲' : ' ▼') : '';

  // All unique providers from data
  const providers = useMemo(() => {
    const seen = new Map();
    etfs.forEach(e => { if (e.provider_name) seen.set(e.provider_name, e.provider_name); });
    return [...seen.keys()].sort();
  }, [etfs]);

  const filtered = useMemo(() => {
    let rows = etfs.filter(e => {
      if (search && !`${e.symbol} ${e.name} ${e.fund_provider || ''} ${e.index_tracked || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (exchFilter && e.exchange !== exchFilter) return false;
      if (provFilter && (e.provider_name || e.fund_provider || '') !== provFilter) return false;
      if (gearedOnly && !e.is_geared) return false;
      if (merMin !== '' && (e.expense_ratio == null || e.expense_ratio < parseFloat(merMin))) return false;
      if (merMax !== '' && (e.expense_ratio == null || e.expense_ratio > parseFloat(merMax))) return false;
      if (aumMin !== '' && (e.aum_usd == null || e.aum_usd < parseFloat(aumMin) * 1e6)) return false;
      if (holdingsMin !== '' && (e.holdings_count || 0) < parseInt(holdingsMin)) return false;
      return true;
    });
    return sortRows(rows, sort.col, sort.dir);
  }, [etfs, search, exchFilter, provFilter, gearedOnly, merMin, merMax, aumMin, holdingsMin, sort]);

  const cols = 9; // number of visible columns

  const TH = ({ col, label }) => (
    <th style={S.th(sort, col)} onClick={() => handleSort(col)}>
      {label}{sortArrow(col)}
    </th>
  );

  return (
    <div style={S.page}>
      <Navbar />

      {/* Filter bar */}
      <div style={S.filters}>
        <input style={{ ...S.input, width: '200px' }} placeholder="Search symbol, name, index…" value={search} onChange={e => setSearch(e.target.value)} />

        <div style={S.divider} />

        {/* Exchange chips */}
        {['AU', 'US'].map(ex => (
          <button key={ex} style={S.chip(exchFilter === ex)} onClick={() => setExchFilter(exchFilter === ex ? '' : ex)}>{ex}</button>
        ))}

        <div style={S.divider} />

        {/* Provider */}
        <select style={S.select} value={provFilter} onChange={e => setProvFilter(e.target.value)}>
          <option value="">All providers</option>
          {providers.map(p => <option key={p}>{p}</option>)}
        </select>

        <div style={S.divider} />

        {/* MER range */}
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" }}>MER%</span>
        <div style={S.rangeWrap}>
          <input style={S.rangeInput} placeholder="min" value={merMin} onChange={e => setMerMin(e.target.value)} type="number" step="0.01" />
          <span style={{ color: 'var(--text-muted)' }}>–</span>
          <input style={S.rangeInput} placeholder="max" value={merMax} onChange={e => setMerMax(e.target.value)} type="number" step="0.01" />
        </div>

        <div style={S.divider} />

        {/* AUM */}
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" }}>AUM $M min</span>
        <input style={S.rangeInput} placeholder="0" value={aumMin} onChange={e => setAumMin(e.target.value)} type="number" />

        <div style={S.divider} />

        {/* Holdings min */}
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: "'DM Mono', monospace" }}>Holdings ≥</span>
        <input style={S.rangeInput} placeholder="0" value={holdingsMin} onChange={e => setHoldingsMin(e.target.value)} type="number" />

        <div style={S.divider} />

        {/* Geared toggle */}
        <button style={S.chip(gearedOnly)} onClick={() => setGearedOnly(v => !v)}>Geared only</button>

        {/* Reset */}
        {(search || exchFilter || provFilter || gearedOnly || merMin || merMax || aumMin || holdingsMin) && (
          <button style={{ ...S.chip(false), borderColor: 'var(--error)', color: 'var(--error)' }}
            onClick={() => { setSearch(''); setExchFilter(''); setProvFilter(''); setGearedOnly(false); setMerMin(''); setMerMax(''); setAumMin(''); setHoldingsMin(''); }}>
            Clear
          </button>
        )}

        <span style={S.count}>{filtered.length} of {etfs.length} ETFs</span>
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        {loading ? (
          <div style={{ padding: '40px 24px', color: 'var(--text-muted)', fontSize: '13px' }}>Loading ETFs…</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
               <th style={{ ...S.th(sort, ''), cursor: 'default', width: '32px' }} />
                <TH col="symbol"        label="Symbol"   />
                <TH col="name"          label="Name"     />
                <TH col="exchange"      label="Exch"     />
                <TH col="provider_name" label="Provider" />
                <TH col="expense_ratio" label="MER %"    />
                <TH col="aum_usd"       label="AUM"      />
                <TH col="ttm_yield"     label="Yield %"  />
                <TH col="holdings_count" label="Holdings" />
              </tr>
            </thead>
            <tbody>
              {filtered.map(etf => {
                const isOpen = expanded[etf.instrument_id];
                return (
                  <React.Fragment key={etf.instrument_id}>
                    <tr
                      style={{ cursor: 'pointer', background: isOpen ? 'var(--accent-dim)' : 'transparent', transition: 'background 0.1s' }}
                      onClick={() => toggleExpand(etf.instrument_id)}
                      onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = 'var(--surface)'; }}
                      onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {/* Expand toggle */}
                      <td style={{ ...S.td, width: '32px', paddingRight: 0, color: 'var(--accent)', fontFamily: "'DM Mono', monospace", fontSize: '12px' }}>
                        {isOpen ? '▾' : '▸'}
                      </td>
                      <td style={{ ...S.td, fontFamily: "'DM Mono', monospace", fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap' }}>
                        {etf.symbol}
                        {etf.is_geared && <span style={{ marginLeft: '5px', fontSize: '9px', color: 'var(--error)', fontWeight: 700 }}>{etf.gearing_ratio}x</span>}
                      </td>
                      <td style={{ ...S.td, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={etf.name}>
                        {etf.name || '—'}
                      </td>
                      <td style={{ ...S.td, fontFamily: "'DM Mono', monospace", color: 'var(--text-muted)' }}>{etf.exchange}</td>
                      <td style={S.td}>{etf.provider_name || etf.fund_provider || '—'}</td>
                      <td style={{ ...S.td, fontFamily: "'DM Mono', monospace" }}>
                        {etf.expense_ratio != null ? `${fmtNum(etf.expense_ratio, 2)}%` : '—'}
                      </td>
                      <td style={{ ...S.td, fontFamily: "'DM Mono', monospace" }}>{fmtAum(etf.aum_usd)}</td>
                      <td style={{ ...S.td, fontFamily: "'DM Mono', monospace" }}>
                        {etf.ttm_yield != null ? `${fmtNum(etf.ttm_yield, 2)}%` : '—'}
                      </td>
                      <td style={{ ...S.td, fontFamily: "'DM Mono', monospace", textAlign: 'right' }}>
                        {etf.holdings_count > 0 ? etf.holdings_count : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                    </tr>

                    {/* Expanded detail + holdings */}
                    {isOpen && <EtfDetailRow etf={etf} colSpan={cols} />}
                  </React.Fragment>
                );
              })}

              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={cols} style={{ ...S.td, textAlign: 'center', color: 'var(--text-muted)', padding: '48px' }}>
                    No ETFs match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
